import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import axios from 'axios';
import { PasswordInput } from '@/components/ui/password-input';
import { Combobox } from '@/components/ui/combobox';
import { Spinner } from '@/components/ui/spinner';
import OnboardingPageHeader from '../ComponentOnboarding/OnboardingPageHeader';
import AddRepositoryListingProgress, {
  type ListingStep,
} from './AddRepositoryListingProgress';
import {
  detectAccessToken,
  isConnectableAccessTokenSource,
  isPendingKnownAccessToken,
} from '@/utils/detectAccessToken';
import {
  getGithubOrganizations,
  getGithubRepositories,
} from '@/components/smartai/api/githubOnboarding';
import { getOrganizationDetails } from '@/components/smartai/api/getOrganizationDetails';
import { getProjectsDetails } from '@/components/smartai/api/getProjectsDetails';
import { getApiErrorMessage } from '@/utils';
import type { ScmProviderId } from '@/types/scm-provider';

const INVALID_TOKEN_MESSAGE = 'Invalid token. please check and try again.';
const EXPIRED_TOKEN_MESSAGE = 'Expired token. please check and try again.';

type OrgOption = {
  value: string;
  label: string;
  accountId?: string;
  accountUri?: string;
};

type AddRepositoryConnectPanelProps = {
  token: string;
  onTokenChange: (token: string) => void;
  onConnect: (
    provider: ScmProviderId,
    pat: string,
    organization: string,
    resources: { value: string; label: string }[],
    organizationAccountId?: string,
    organizationAccountUri?: string,
  ) => void;
  resetListingKey?: number;
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const formatDuration = (ms: number) => `${Math.max(0, ms / 1000).toFixed(1)}s`;

function isExpiredTokenError(err: unknown): boolean {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  const message = getApiErrorMessage(err, '').toLowerCase();
  return (
    status === 401 ||
    message.includes('expired') ||
    message.includes('unauthorized') ||
    message.includes('invalid or expired')
  );
}

async function loadResources(
  provider: ScmProviderId,
  pat: string,
  organization: string,
): Promise<{ value: string; label: string }[]> {
  if (provider === 'github') {
    const res = await getGithubRepositories('github', pat, organization);
    return (res.data?.repositories ?? []).map(repo => ({
      value: repo.name,
      label: repo.name,
    }));
  }

  const projectsRes = await getProjectsDetails({
    provider: 'azure-devops',
    body: { organization },
    requesterToken: { patToken: pat },
  });
  return (projectsRes.data?.projects?.value ?? []).map(project => ({
    value: project.name,
    label: project.name,
  }));
}

const AddRepositoryConnectPanel = ({
  token,
  onTokenChange,
  onConnect,
  resetListingKey = 0,
}: AddRepositoryConnectPanelProps) => {
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [expiredError, setExpiredError] = useState<string | null>(null);
  const [listingSteps, setListingSteps] = useState<ListingStep[] | null>(null);
  const [listingError, setListingError] = useState<string | null>(null);
  const listingRunRef = useRef(0);
  const onConnectRef = useRef(onConnect);
  const recognitionMsRef = useRef(0);
  const resourceStartedAtRef = useRef(0);
  onConnectRef.current = onConnect;

  const trimmed = token.trim();
  const detected = detectAccessToken(trimmed);
  const connectable = isConnectableAccessTokenSource(detected?.source);
  const showInvalidToken =
    trimmed.length > 0 &&
    !detected &&
    !isPendingKnownAccessToken(trimmed);
  const showRecognition = Boolean(detected) && !showInvalidToken;
  const showOrgSection =
    connectable && showRecognition && !expiredError && !orgsLoading && orgs.length > 0;
  const listingBusy =
    listingSteps?.some(step => step.state === 'active') ?? false;
  const listingActive = listingSteps !== null && !listingError;

  useEffect(() => {
    if (!connectable || !detected) {
      setOrgs([]);
      setSelectedOrg('');
      setOrgsLoading(false);
      setExpiredError(null);
      setListingSteps(null);
      setListingError(null);
      recognitionMsRef.current = 0;
      return;
    }

    const provider = detected.source;
    const pat = trimmed;
    let cancelled = false;
    listingRunRef.current += 1;
    setOrgs([]);
    setSelectedOrg('');
    setOrgsLoading(true);
    setExpiredError(null);
    setListingSteps(null);
    setListingError(null);
    recognitionMsRef.current = 0;

    const load = async () => {
      const started = performance.now();
      try {
        if (provider === 'github') {
          const res = await getGithubOrganizations('github', pat);
          if (res.success === false) throw new Error(EXPIRED_TOKEN_MESSAGE);
          const list = res.data?.organizations ?? [];
          const options = list.map(org => ({
            value: org.login,
            label:
              org.type === 'User'
                ? `${org.login} (you)`
                : org.name?.trim() || org.login,
          }));
          if (cancelled) return;
          recognitionMsRef.current = performance.now() - started;
          setOrgs(options);
        } else {
          const res = await getOrganizationDetails('azure-devops', pat);
          if (res.success === false) throw new Error(EXPIRED_TOKEN_MESSAGE);
          const list = res.data?.value ?? [];
          const options = list.map(org => ({
            value: org.accountName,
            label: org.accountName,
            accountId: org.accountId,
            accountUri: org.accountUri,
          }));
          if (cancelled) return;
          recognitionMsRef.current = performance.now() - started;
          setOrgs(options);
        }
      } catch (err) {
        if (cancelled) return;
        setOrgs([]);
        setSelectedOrg('');
        setExpiredError(
          isExpiredTokenError(err) ? EXPIRED_TOKEN_MESSAGE : EXPIRED_TOKEN_MESSAGE,
        );
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [connectable, detected?.source, trimmed]);

  useEffect(() => {
    if (!resetListingKey) return;
    listingRunRef.current += 1;
    setListingSteps(null);
    setListingError(null);
  }, [resetListingKey]);

  useEffect(() => {
    if (!listingBusy) return;
    const id = window.setInterval(() => {
      const elapsed = performance.now() - resourceStartedAtRef.current;
      setListingSteps(prev =>
        prev?.map(step =>
          step.id === 'resources' && step.state === 'active'
            ? { ...step, duration: formatDuration(elapsed) }
            : step,
        ) ?? null,
      );
    }, 100);
    return () => window.clearInterval(id);
  }, [listingBusy]);

  const startListing = async (orgOverride?: string) => {
    const orgValue = orgOverride ?? selectedOrg;
    if (!detected || !isConnectableAccessTokenSource(detected.source) || !orgValue) {
      return;
    }
    const provider = detected.source;
    const pat = trimmed;
    const orgOption = orgs.find(org => org.value === orgValue);
    const orgLabel = orgOption?.label.replace(/ \(you\)$/, '') || orgValue;
    const isGithub = provider === 'github';
    const resourceStepTitle = isGithub ? 'Listing repositories' : 'Listing projects';
    const runId = listingRunRef.current + 1;
    listingRunRef.current = runId;
    const isCurrent = () => listingRunRef.current === runId;
    const acceptedDuration = formatDuration(recognitionMsRef.current);

    setListingError(null);
    setListingSteps([
      {
        id: 'token',
        title: 'Token accepted',
        detail: orgLabel,
        duration: acceptedDuration,
        state: 'done',
      },
      {
        id: 'organisation',
        title: 'Opened organisation',
        detail: `${orgs.length || 1} organisation${
          orgs.length === 1 ? '' : 's'
        } visible to this token`,
        duration: acceptedDuration,
        state: 'done',
      },
      {
        id: 'resources',
        title: resourceStepTitle,
        detail: isGithub
          ? `Loading repositories in ${orgLabel}`
          : `Loading projects in ${orgLabel}`,
        duration: '0.0s',
        state: 'active',
      },
    ]);

    resourceStartedAtRef.current = performance.now();

    try {
      const resources = await loadResources(provider, pat, orgValue);
      if (!isCurrent()) return;
      const count = resources.length;
      const noun =
        count === 1
          ? isGithub
            ? 'repository'
            : 'project'
          : isGithub
            ? 'repositories'
            : 'projects';
      setListingSteps(prev =>
        prev?.map(step =>
          step.id === 'resources'
            ? {
                ...step,
                state: 'done',
                detail: `${count} ${noun} in ${orgLabel}`,
                duration: formatDuration(
                  performance.now() - resourceStartedAtRef.current,
                ),
              }
            : step,
        ) ?? null,
      );
      await wait(650);
      if (!isCurrent()) return;
      const selectedOrgOption = orgs.find(org => org.value === orgValue);
      onConnectRef.current(
        provider,
        pat,
        orgValue,
        resources,
        selectedOrgOption?.accountId,
        selectedOrgOption?.accountUri,
      );
    } catch (err) {
      if (!isCurrent()) return;
      setListingSteps(prev =>
        prev?.map(step =>
          step.id === 'resources' ? { ...step, state: 'pending' } : step,
        ) ?? null,
      );
      setListingError(
        getApiErrorMessage(
          err,
          isGithub
            ? 'Could not load repositories. Please try again.'
            : 'Could not load projects. Please try again.',
        ),
      );
    }
  };

  const errorMessage = showInvalidToken
    ? INVALID_TOKEN_MESSAGE
    : expiredError;
  const isGithub = detected?.source === 'github';

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--inv-bg)]">
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-6 pt-8 max-[520px]:px-5">
        <OnboardingPageHeader
          eyebrow="Add Repository"
          title="Connect a source"
        />

        <div className="mt-2">
          <label
            htmlFor="add-repository-access-token"
            className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
            Access Token
          </label>
          <PasswordInput
            id="add-repository-access-token"
            title="Access Token"
            placeholder="Enter your GitHub, Azure DevOps token"
            value={token}
            disabled={listingActive}
            onChange={value => {
              onTokenChange(value);
            }}
            className={
              showRecognition
                ? 'font-mono border-[var(--inv-accent)] focus-visible:ring-[var(--inv-accent-ring)]'
                : 'font-mono'
            }
          />
          {showRecognition ? (
            <div
              className="mt-3 flex items-center gap-2.5 rounded-[10px] border border-solid border-[#abefc6] bg-[#ecfdf3] px-4 py-3"
              role="status"
              aria-live="polite">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#12b76a] text-white">
                <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
              </span>
              <p className="m-0 text-sm font-semibold text-[#067647]">
                {detected?.label}
              </p>
            </div>
          ) : null}
          {errorMessage ? (
            <p className="mt-2 m-0 text-[0.8125rem] text-[#b42318]" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>

        {showOrgSection ? (
          <div className="mt-6">
            <label
              htmlFor="add-repository-organization"
              className="mb-1.5 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
              Organization
            </label>
            {!isGithub ? (
              <p className="mb-2.5 m-0 text-[0.8125rem] leading-[1.5] text-[var(--inv-text-muted)]">
                Azure DevOps tokens are scoped to one organization, and the
                token does not say which.
              </p>
            ) : null}
            <div className="relative [&_button_span]:font-mono">
              <Combobox
                id="add-repository-organization"
                data={orgs}
                value={selectedOrg}
                contentClassName="font-mono [&_input]:font-mono [&_button]:font-mono [&_span]:font-mono"
                onSelect={val => {
                  const nextOrg = String(val);
                  setSelectedOrg(nextOrg);
                  listingRunRef.current += 1;
                  setListingSteps(null);
                  setListingError(null);
                  void startListing(nextOrg);
                }}
                placeholder="Select an Organization"
                disabled={listingActive}
              />
            </div>
          </div>
        ) : connectable && showRecognition && !expiredError && orgsLoading ? (
          <div className="mt-6 flex items-center gap-2 text-[0.8125rem] text-[var(--inv-text-muted)]">
            <Spinner compact />
            Loading Organizations...
          </div>
        ) : null}

        {listingSteps ? (
          <AddRepositoryListingProgress
            heading={
              isGithub ? 'Loading repositories' : 'Loading Projects'
            }
            subheading={
              isGithub
                ? 'Fetching Repositories available to you'
                : 'Fetching Projects available to you'
            }
            steps={listingSteps}
            error={listingError}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AddRepositoryConnectPanel;
